/**
 * Settings, including the one-off import of the legacy Replit/Neon data.
 */
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Moon, Sun, Monitor } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import { useWorkMode, WORK_MODES } from "@/hooks/use-work-mode";
import { LegacyImport } from "@/components/legacy-import";
import { cn } from "@/lib/utils";

export default function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { workMode, setWorkMode } = useWorkMode();
  const me = useQuery(api.users.me);

  return (
    <div className="space-y-5 max-w-3xl">
      <h1 className="page-title text-2xl font-semibold">Settings</h1>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p>{me?.displayName ?? "—"}</p>
          <p className="text-muted-foreground">{me?.email ?? "—"}</p>
          <p className="text-xs text-muted-foreground pt-2">
            Timezone: <span className="font-mono">{me?.timezone ?? "—"}</span>{" "}
            &middot; used for Dashplan day boundaries and the completed log.
          </p>
          <p className="text-xs text-muted-foreground">
            Profile and password are managed in Clerk — use the avatar menu.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Appearance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {(
              [
                { value: "light", label: "Light", icon: Sun },
                { value: "dark", label: "Dark", icon: Moon },
                { value: "system", label: "System", icon: Monitor },
              ] as const
            ).map(({ value, label, icon: Icon }) => (
              <Button
                key={value}
                variant={theme === value ? "default" : "outline"}
                size="sm"
                onClick={() => setTheme(value)}
              >
                <Icon className="w-4 h-4 mr-1.5" />
                {label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Default work mode</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {WORK_MODES.map((mode) => {
              const Icon = mode.icon;
              const selected = workMode === mode.value;
              return (
                <Button
                  key={mode.value}
                  variant="outline"
                  size="sm"
                  onClick={() => setWorkMode(mode.value)}
                  className={cn(
                    "gap-1.5 border-2",
                    selected
                      ? cn(mode.border, mode.text, "font-medium")
                      : "text-muted-foreground",
                  )}
                  aria-pressed={selected}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {mode.label}
                </Button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Saved to your account, so it follows you between devices.
          </p>
        </CardContent>
      </Card>

      <LegacyImport />
    </div>
  );
}
